/**
 * LECashToClose - Cash to close calculation section
 *
 * Shows the breakdown of costs and credits using HUD/1003 settlement
 * statement fields. Single list layout: costs positive, credits negative,
 * only non-zero rows displayed. Purchase Price hidden for refinances.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Wallet, ArrowDown, ArrowUp } from 'lucide-react';
import { LESection } from './LESection';
import { LEFeeRow } from './LEFeeRow';
import type { ParsedLoanEstimate } from './types';

interface LECashToCloseProps {
  data: ParsedLoanEstimate;
  className?: string;
}

/**
 * Format currency for display (always show cents)
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

export const LECashToClose: React.FC<LECashToCloseProps> = ({
  data,
  className
}) => {
  const ctc = data.cashToClose;

  const isFromBorrower = ctc.cashToClose >= 0;

  // Build line items: costs (positive), then credits (negative)
  // Only show non-zero values
  const costItems: { label: string; amount: number }[] = [];
  const creditItems: { label: string; amount: number }[] = [];

  // Costs
  if (ctc.isPurchase && ctc.purchasePrice > 0) {
    costItems.push({ label: 'Purchase Price', amount: ctc.purchasePrice });
  }
  if (ctc.estimatedClosingCosts > 0) {
    costItems.push({ label: 'Estimated Closing Costs', amount: ctc.estimatedClosingCosts });
  }
  if (ctc.estimatedPrepaids > 0) {
    costItems.push({ label: 'Estimated Prepaids', amount: ctc.estimatedPrepaids });
  }
  if (ctc.mipFundingFee > 0) {
    costItems.push({ label: 'MIP/PMI/Funding Fee', amount: ctc.mipFundingFee });
  }
  if (ctc.discountPoints > 0) {
    costItems.push({ label: 'Discount Points', amount: ctc.discountPoints });
  }

  // Credits
  if (ctc.loanAmount > 0) {
    creditItems.push({ label: 'Loan Amount', amount: ctc.loanAmount });
  }
  if (ctc.sellerPaidCosts > 0) {
    creditItems.push({ label: 'Seller-Paid Costs', amount: ctc.sellerPaidCosts });
  }
  if (ctc.lenderCredit > 0) {
    creditItems.push({ label: 'Lender Credit', amount: ctc.lenderCredit });
  }
  if (ctc.sellerCredit > 0) {
    creditItems.push({ label: 'Seller Credit', amount: ctc.sellerCredit });
  }
  if (ctc.deposit > 0) {
    creditItems.push({ label: 'Deposit / Earnest Money', amount: ctc.deposit });
  }
  // 1852 = LENPCC + BKRPCC + OTHPCC — show breakdown when available, otherwise combined total
  const hasIndividualPaidCC = ctc.lenderPaidCC > 0 || ctc.brokerPaidCC > 0 || ctc.otherPaidCC > 0;
  if (hasIndividualPaidCC) {
    if (ctc.lenderPaidCC > 0) {
      creditItems.push({ label: 'Lender-Paid Closing Costs', amount: ctc.lenderPaidCC });
    }
    if (ctc.brokerPaidCC > 0) {
      creditItems.push({ label: 'Broker-Paid Closing Costs', amount: ctc.brokerPaidCC });
    }
    if (ctc.otherPaidCC > 0) {
      creditItems.push({ label: 'Other-Paid Closing Costs', amount: ctc.otherPaidCC });
    }
  } else if (ctc.lenderBrokerOtherPaid > 0) {
    creditItems.push({ label: 'Lender/Broker/Other Paid', amount: ctc.lenderBrokerOtherPaid });
  }
  // Note: otherCredits (URLA.X149) is the sum of M1-M6 lines — NOT displayed
  // because we already show the individual components (deposit, other assets,
  // lender/seller credit, additional credits, refunded MIP, 1852)
  if (ctc.otherAssets > 0) {
    creditItems.push({ label: 'Other Assets Applied', amount: ctc.otherAssets });
  }
  if (ctc.refundedMIP > 0) {
    creditItems.push({ label: 'Refunded MIP', amount: ctc.refundedMIP });
  }
  if (ctc.piggybackClosingCost > 0) {
    creditItems.push({ label: 'Piggyback Loan', amount: ctc.piggybackClosingCost });
  }
  // Additional credits (up to 4 line items)
  ctc.additionalCredits.forEach((amt, idx) => {
    if (amt > 0) {
      creditItems.push({ label: `Additional Credit ${idx + 1}`, amount: amt });
    }
  });

  return (
    <LESection
      title="Calculating Cash to Close"
      icon={<Wallet className="h-4 w-4" />}
      className={className}
    >
      <div className="space-y-1">
        {/* Cost line items */}
        {costItems.map((item, idx) => (
          <LEFeeRow key={`cost-${idx}`} description={item.label} amount={item.amount} />
        ))}

        {/* Total Costs subtotal */}
        {ctc.totalCosts > 0 && (
          <LEFeeRow description="Total Costs" amount={ctc.totalCosts} isSubtotal />
        )}

        {/* Credit line items (shown as negative) */}
        {creditItems.map((item, idx) => (
          <LEFeeRow key={`credit-${idx}`} description={item.label} amount={item.amount} negative />
        ))}

        {/* Total Credits subtotal */}
        {ctc.totalCredits > 0 && (
          <LEFeeRow description="Total Credits" amount={ctc.totalCredits} isSubtotal negative />
        )}

        {/* Cash to Close result */}
        <div className="pt-3 mt-3 border-t-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isFromBorrower ? (
                <ArrowUp className="h-5 w-5 text-amber-500" />
              ) : (
                <ArrowDown className="h-5 w-5 text-green-500" />
              )}
              <div>
                <p className="font-semibold">Estimated Cash to Close</p>
                <p className="text-xs text-muted-foreground">
                  {isFromBorrower ? 'From Borrower' : 'To Borrower'}
                </p>
              </div>
            </div>
            <span
              className={cn(
                'text-xl font-bold tabular-nums',
                isFromBorrower ? 'text-foreground' : 'text-green-600'
              )}
            >
              {formatCurrency(Math.abs(ctc.cashToClose))}
            </span>
          </div>
        </div>
      </div>
    </LESection>
  );
};

export default LECashToClose;
